import { globalConfig, i18n } from '@/lib/config'
import { match as matchLocale } from '@formatjs/intl-localematcher'
import { type ClassValue, clsx } from 'clsx'
import { format as formatTime } from 'date-fns'
import Negotiator from 'negotiator'
import type { NextRequest } from 'next/server'
import { twMerge } from 'tailwind-merge'

// Function to merge class names using clsx and tailwind-merge
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Function to format a timestamp into ""yyyy-MMM-d h:mm a" date format
export function formatTimestamp(timestamp: number | string) {
  if (!timestamp) return timestamp

  const date =
    typeof timestamp === 'string' && timestamp.includes('T')
      ? new Date(timestamp)
      : new Date(Number(timestamp) * 1000)

  return formatTime(date, globalConfig.dateTimeFormat)
}

export const formatDateTime = (dateTime: Date) => {
  return formatTime(dateTime, globalConfig.dateTimeFormat)
}

// Function to format a price with a specific number of decimal places
export function formatPrice(price: number | string, digits = 2, showCurrency = true, showDecimal = true) {
  // Convert to number and handle edge cases
  const numPrice = Number(price)
  
  if (Number.isNaN(numPrice) || numPrice === 0 || !numPrice) {
    return showCurrency ? 'Rs.0.00' : '0.00'
  }

  // Format number with fixed decimal places
  const fixedNumber = showDecimal ? numPrice.toFixed(digits) : numPrice.toFixed(0)
  
  // Split into integer and decimal parts
  const [integerPart, decimalPart] = fixedNumber.split('.')
  
  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  
  // Combine integer and decimal parts
  const formattedNumber = decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger

  if (!showCurrency) {
    return formattedNumber
  }

  return `Rs.${formattedNumber}`
}

// Function to format a number as K, M, B, T, etc.
export function shortenNumber(number: number) {
  if (number === 0 || !number) return '0'
  const units = ['', 'K', 'M', 'B', 'T']
  const unit = Math.floor((number.toFixed(0).length - 1) / 3)
  const num = (number / Math.pow(1000, unit)).toFixed(1)
  return num.replace(/\.0$/, '') + units[unit]
}

export const mockDelay = (delay: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve()
    }, delay)
  })
}

export function getLocale(request: NextRequest): string | undefined {
  const negotiatorHeaders: Record<string, string> = {}
  request.headers.forEach((value, key) => (negotiatorHeaders[key] = value))

  // @ts-ignore locales are readonly
  const locales: string[] = i18n.locales

  let languages = new Negotiator({ headers: negotiatorHeaders }).languages(
    locales,
  )

  const locale = matchLocale(languages, locales, i18n.defaultLocale)

  return locale
}

// Allow only numbers to be entered in an input field
export function handleNumberInput(e: React.KeyboardEvent<HTMLInputElement>) {
  const allowedKeys = [
    'Enter',
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowUp',
    'ArrowDown',
    'ArrowRight',
    '.',
  ]
  const isNumberKey = e.key >= '0' && e.key <= '9'

  // Allow cmd/ctrl + a, cmd/ctrl + c, cmd/ctrl + v
  if (e.metaKey || e.ctrlKey) {
    if (['a', 'c', 'v'].includes(e.key)) return
  }

  // Prevent multiple decimal points
  if (e.key === '.' && (e.target as HTMLInputElement).value.includes('.')) {
    e.preventDefault()
    return
  }

  if (!(isNumberKey || allowedKeys.includes(e.key))) {
    e.preventDefault()
  }
}

// Utility to send Error Response with status code from API
export function ErrorResponse({
  message,
  status = 500,
}: {
  message: string
  status?: number
}) {
  return new Response(message, {
    status,
    headers: { 'Content-Type': 'application/json' },
  })
}

// Utility to send Success Response with data from API
export function SuccessResponse(data: any) {
  return new Response(JSON.stringify(data), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  })
}
